import React from 'react';
import { useFormikContext } from 'formik';
import { 
  Box, 
  Typography, 
  Paper, 
  Divider, 
  Chip,
  Stack,
  Avatar,
  IconButton,
  Tooltip
} from '@mui/material';
import { format } from 'date-fns';
import { motion } from 'framer-motion';

// Icons
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import LuggageIcon from '@mui/icons-material/Luggage';
import PaymentIcon from '@mui/icons-material/Payment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';

import { BookingFormValues } from '../../pages/BookingPage';
import GridItem from '../common/GridItem';
import GridContainer from '../common/GridContainer';

// Sample vehicle data for the summary
const vehicles = {
  'luxury-sedan': {
    name: 'Luxury Sedan',
    hourlyRate: 6225,
    distanceRate: 291,
    image: '/images/vehicles/luxury-sedan.jpg'
  },
  'executive-suv': {
    name: 'Executive SUV',
    hourlyRate: 7885,
    distanceRate: 353,
    image: '/images/vehicles/executive-suv.jpg'
  },
  'luxury-van': {
    name: 'Luxury Van',
    hourlyRate: 10375,
    distanceRate: 457,
    image: '/images/vehicles/luxury-van.jpg'
  },
  'stretch-limousine': {
    name: 'Stretch Limousine',
    hourlyRate: 12450,
    distanceRate: 519,
    image: '/images/vehicles/stretch-limousine.jpg'
  }
};

const BookingSummary: React.FC = () => {
  const { values } = useFormikContext<BookingFormValues>();
  
  // Calculate estimated price
  const calculatePrice = () => {
    if (!values.vehicleId) return 0;
    
    const vehicle = vehicles[values.vehicleId as keyof typeof vehicles];
    if (!vehicle) return 0;
    
    if (values.tripType === 'hourly') {
      return vehicle.hourlyRate * values.hours;
    } else {
      const basePrice = vehicle.distanceRate * 20;
      return values.tripType === 'round-trip' ? basePrice * 2 : basePrice;
    }
  };
  
  const estimatedPrice = calculatePrice();
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Stack spacing={4}>
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
            Review Your Booking
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Please review all details before confirming your booking
          </Typography>
        </Box>

        {/* Trip Details Card */}
        <Paper 
          elevation={2} 
          sx={{ 
            p: 3, 
            borderRadius: 2,
            background: 'linear-gradient(145deg, #ffffff 0%, #f5f5f5 100%)'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Trip Details
            </Typography>
            <Chip
              label={values.tripType === 'one-way' 
                ? 'One-way Trip' 
                : values.tripType === 'round-trip' 
                  ? 'Round-trip' 
                  : 'Hourly Charter'}
              color="primary"
              sx={{ fontWeight: 500 }}
            />
          </Box>

          <GridContainer spacing={3}>
            <GridItem xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.light', mr: 2 }}>
                  <LocationOnIcon />
                </Avatar>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Pickup Location
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {values.pickupLocation}
                  </Typography>
                </Box>
              </Box>
            </GridItem>

            <GridItem xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.light', mr: 2 }}>
                  <LocationOnIcon />
                </Avatar>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Drop-off Location
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {values.dropoffLocation}
                  </Typography>
                </Box>
              </Box>
            </GridItem>

            <GridItem xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: 'primary.light', mr: 2 }}>
                  <CalendarTodayIcon />
                </Avatar>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Pickup Date & Time
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {values.pickupDate ? format(values.pickupDate, 'EEEE, MMMM d, yyyy') : 'No date selected'} at {values.pickupTime || 'No time selected'}
                  </Typography>
                </Box>
              </Box>
            </GridItem>

            {values.tripType === 'round-trip' && values.returnDate && (
              <GridItem xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: 'primary.light', mr: 2 }}>
                    <CalendarTodayIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Return Date & Time
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {format(values.returnDate, 'EEEE, MMMM d, yyyy')} at {values.returnTime || 'No time selected'}
                    </Typography>
                  </Box>
                </Box>
              </GridItem>
            )}

            {values.tripType === 'hourly' && (
              <GridItem xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: 'primary.light', mr: 2 }}>
                    <AccessTimeIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Duration
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {values.hours} {values.hours === 1 ? 'hour' : 'hours'}
                    </Typography>
                  </Box>
                </Box>
              </GridItem>
            )}

            <GridItem xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: 'primary.light', mr: 2 }}>
                  <PersonIcon />
                </Avatar>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Passengers & Luggage
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {values.passengers} {values.passengers === 1 ? 'passenger' : 'passengers'} • {values.luggage} {values.luggage === 1 ? 'piece' : 'pieces'} of luggage
                  </Typography>
                </Box>
              </Box>
            </GridItem>
          </GridContainer>
        </Paper>

        {/* Vehicle Selection Card */}
        <Paper 
          elevation={2} 
          sx={{ 
            p: 3, 
            borderRadius: 2,
            background: 'linear-gradient(145deg, #ffffff 0%, #f5f5f5 100%)'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Vehicle Selection
            </Typography>
            {values.vehicleId && (
              <Tooltip title="Vehicle details">
                <IconButton>
                  <InfoIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {values.vehicleId && vehicles[values.vehicleId as keyof typeof vehicles] ? (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar 
                src={vehicles[values.vehicleId as keyof typeof vehicles].image}
                sx={{ width: 80, height: 80, mr: 3 }}
              />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {vehicles[values.vehicleId as keyof typeof vehicles].name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {values.tripType === 'hourly' 
                    ? `₹${vehicles[values.vehicleId as keyof typeof vehicles].hourlyRate}/hour`
                    : `₹${vehicles[values.vehicleId as keyof typeof vehicles].distanceRate}/km`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Capacity: {values.passengers} passengers • {values.luggage} pieces of luggage
                </Typography>
              </Box>
            </Box>
          ) : (
            <Typography variant="body1" color="error">
              No vehicle selected
            </Typography>
          )}
        </Paper>

        {/* Passenger Details Card */}
        <Paper 
          elevation={2} 
          sx={{ 
            p: 3, 
            borderRadius: 2,
            background: 'linear-gradient(145deg, #ffffff 0%, #f5f5f5 100%)'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Passenger Details
            </Typography>
            <Tooltip title="Edit passenger details">
              <IconButton>
                <EditIcon />
              </IconButton>
            </Tooltip>
          </Box>

          <GridContainer spacing={3}>
            <GridItem xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.light', mr: 2 }}>
                  <PersonIcon />
                </Avatar>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Passenger Name
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {values.firstName} {values.lastName}
                  </Typography>
                </Box>
              </Box>
            </GridItem>

            <GridItem xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.light', mr: 2 }}>
                  <PersonIcon />
                </Avatar>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Contact Information
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {values.email}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {values.phone}
                  </Typography>
                </Box>
              </Box>
            </GridItem>

            {values.companyName && (
              <GridItem xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: 'primary.light', mr: 2 }}>
                    <PersonIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Company Name
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {values.companyName}
                    </Typography>
                  </Box>
                </Box>
              </GridItem>
            )}

            {values.specialRequests && (
              <GridItem xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Avatar sx={{ bgcolor: 'primary.light', mr: 2, mt: 0.5 }}>
                    <InfoIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Special Requests
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {values.specialRequests}
                    </Typography>
                  </Box>
                </Box>
              </GridItem>
            )}
          </GridContainer>
        </Paper>

        {/* Payment Details Card */}
        <Paper 
          elevation={2} 
          sx={{ 
            p: 3, 
            borderRadius: 2,
            background: 'linear-gradient(145deg, #ffffff 0%, #f5f5f5 100%)'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Payment Details
            </Typography>
            <Chip
              label={values.paymentMethod === 'card' ? 'Credit Card' : 'Corporate Account'}
              color="primary"
              sx={{ fontWeight: 500 }}
            />
          </Box>

          {values.paymentMethod === 'card' && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.light', mr: 2 }}>
                <PaymentIcon />
              </Avatar>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Card Details
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  •••• •••• •••• {values.cardNumber.slice(-4)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Expires: {values.expiryDate}
                </Typography>
              </Box>
            </Box>
          )}

          {values.paymentMethod === 'corporate' && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.light', mr: 2 }}>
                <PaymentIcon />
              </Avatar>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Corporate Account
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {values.companyName || 'Company Account'}
                </Typography>
              </Box>
            </Box>
          )}

          {values.acceptTerms && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
              <CheckCircleIcon color="success" sx={{ mr: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Terms and conditions accepted
              </Typography>
            </Box>
          )}
        </Paper>

        {/* Price Summary Card */}
        <Paper 
          elevation={3} 
          sx={{ 
            p: 3, 
            borderRadius: 2,
            background: 'linear-gradient(145deg, #1976d2 0%, #1565c0 100%)',
            color: 'white'
          }}
        >
          <GridContainer alignItems="center" justifyContent="space-between">
            <GridItem>
              <Typography variant="h6" gutterBottom>
                Estimated Total
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                {values.tripType === 'hourly' 
                  ? `${values.hours} hours @ ₹${values.vehicleId ? vehicles[values.vehicleId as keyof typeof vehicles]?.hourlyRate : 0}/hour`
                  : values.tripType === 'round-trip'
                    ? 'Round-trip fare'
                    : 'One-way fare'
                }
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
                {values.passengers} {values.passengers === 1 ? 'passenger' : 'passengers'} • {values.luggage} {values.luggage === 1 ? 'piece' : 'pieces'} of luggage
              </Typography>
            </GridItem>
            <GridItem>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                ₹{estimatedPrice.toFixed(2)}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8, textAlign: 'right' }}>
                {values.paymentMethod === 'corporate' ? 'Charged to corporate account' : 'Charged to credit card'}
              </Typography>
            </GridItem>
          </GridContainer>
        </Paper>

        {/* Final Confirmation Card */}
        <Paper 
          elevation={2} 
          sx={{ 
            p: 3, 
            borderRadius: 2,
            background: 'linear-gradient(145deg, #ffffff 0%, #f5f5f5 100%)'
          }}
        >
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
            Final Confirmation
          </Typography>
          
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Please review all the details above carefully. By proceeding, you confirm that:
          </Typography>
          
          <Stack spacing={2} sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CheckCircleIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="body1">
                All trip details are correct
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CheckCircleIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="body1">
                The selected vehicle meets your requirements
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CheckCircleIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="body1">
                Your contact information is accurate
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CheckCircleIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="body1">
                You agree to the terms and conditions
              </Typography>
            </Box>
          </Stack>
          
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            Note: Once confirmed, changes to your booking may be subject to fees.
          </Typography>
        </Paper>
      </Stack>
    </motion.div>
  );
};

export default BookingSummary; 